import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import ReactDOM from "react-dom";
import type { EmojiClickData } from "emoji-picker-react";
import styled from "styled-components";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import type { KendoEditorInternal } from "../utils/domHelpers";
import { ConversationDetailsContext } from "../../../context";
import {
  EmojiPicker,
  emojiPickerHasError
} from "../../emoji-picker/emoji-picker";
import { EmojiIcon } from "../../emoji-picker/emoji-icon";

const EmojiPickerWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const EmojiButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ebebeb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmojiPickerPopup = styled.div<{
  appRect: DOMRect | null;
  portalTarget: "overlay" | "conversation" | "body";
}>`
  /* Use absolute positioning for overlays (portaled inside container), fixed for others */
  position: ${props =>
    props.portalTarget === "overlay" ? "absolute" : "fixed"};
  z-index: 10001;

  ${props => {
    // For overlay portal target, use simple centered positioning relative to the container
    if (props.portalTarget === "overlay") {
      return `
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      `;
    }

    // For conversation/body portal targets, use fixed positioning with viewport coordinates
    if (props.appRect) {
      return `
        top: ${props.appRect.top + props.appRect.height / 2}px;
        left: ${props.appRect.left + props.appRect.width / 2}px;
        transform: translate(-50%, -50%);
      `;
    }

    // Fallback to centered
    return `
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    `;
  }}

  max-height: 90vh;
  overflow-y: auto;
`;

export interface CustomEmojiPickerProps {
  /** ProseMirror view from Kendo Editor - used to insert emojis into the document */
  view?: KendoEditorInternal["view"];
}

/**
 * Custom Emoji Picker component for Kendo Editor toolbar
 * Provides emoji selection functionality that inserts emojis at cursor position
 */
export const CustomEmojiPicker: React.FC<CustomEmojiPickerProps> = ({
  view
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [appRect, setAppRect] = useState<DOMRect | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [portalTarget, setPortalTarget] = useState<
    "overlay" | "conversation" | "body"
  >("body");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { "nx.cxm.show-emoji-email": showEmojiPicker } = useCxmFlags();
  const { setEmojiPickerError } = useContext(ConversationDetailsContext);

  useEffect(() => {
    if (emojiPickerHasError && !hasLoadError) {
      setHasLoadError(true);
      setIsOpen(false);
      setEmojiPickerError(true);
    }
  }, [isOpen, hasLoadError, setEmojiPickerError]);

  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      if (!view || !view.state || !view.dispatch) return;

      const { state, dispatch } = view;
      const emoji = emojiData.emoji;

      // Create emoji text node
      let textNode: unknown;
      try {
        const schema = state.schema;
        if (!schema || !schema.text) {
          throw new Error("Schema or text method not available");
        }
        textNode = schema.text(emoji);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error creating emoji text node:", error);
        setEmojiPickerError(true);
        setIsOpen(false);
        return;
      }

      // Insert emoji into editor
      try {
        const tr = state.tr;
        if (!tr || !tr.replaceSelectionWith) {
          throw new Error("Transaction or replaceSelectionWith not available");
        }
        const transaction = tr.replaceSelectionWith(textNode, false);
        dispatch(transaction);

        // Clear any previous errors on successful insertion
        setEmojiPickerError(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error inserting emoji into editor:", error);
        setEmojiPickerError(true);
        setIsOpen(false);
        return;
      }

      setIsOpen(false);

      // Return focus to editor (deferred to allow DOM updates)
      setTimeout(() => {
        try {
          if (view.focus) {
            view.focus();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error refocusing editor:", error);
          // Don't show alert for focus errors - they're non-critical
        }
      }, 0);
    },
    [view, setEmojiPickerError]
  );
  const togglePicker = () => {
    if (!isOpen) {
      // Prevent opening if previously failed to load
      if (hasLoadError || emojiPickerHasError) {
        setHasLoadError(true);
        return;
      }

      // Always prefer conversation-list-reply-pane as it's the most specific container
      const conversationContainer = document.getElementById(
        "conversation-list-reply-pane"
      );
      const customerCardContainer = document.getElementById(
        "customer-card-container"
      );

      // Determine which container we're using and set portal target type
      if (conversationContainer) {
        // Check if we're inside a customer card overlay by seeing if both exist
        const isInOverlay = customerCardContainer !== null;
        setPortalTarget(isInOverlay ? "overlay" : "conversation");
        setAppRect(conversationContainer.getBoundingClientRect());
      } else if (customerCardContainer) {
        setPortalTarget("overlay");
        setAppRect(customerCardContainer.getBoundingClientRect());
      } else {
        setPortalTarget("body");
        setAppRect(null);
      }
    }
    setIsOpen(prev => !prev);
  };

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        const emojiPicker = document.querySelector(".EmojiPickerReact");
        if (emojiPicker && !emojiPicker.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const pickerContent = isOpen ? (
    <EmojiPickerPopup
      appRect={appRect}
      data-emoji-picker-portal="true"
      portalTarget={portalTarget}
    >
      <EmojiPicker
        data-testid="custom-emoji-picker-shared"
        height={400}
        lazyLoadEmojis={true}
        width={350}
        onEmojiClick={handleEmojiClick}
      />
    </EmojiPickerPopup>
  ) : null;

  if (!showEmojiPicker) {
    return null;
  }

  return (
    <EmojiPickerWrapper>
      <EmojiButton
        aria-label="Insert Emoji"
        data-testid="emoji-picker-button"
        ref={buttonRef}
        title="Insert Emoji"
        type="button"
        onClick={togglePicker}
      >
        <EmojiIcon color="#424242" />
      </EmojiButton>
      {pickerContent &&
        ReactDOM.createPortal(
          pickerContent,
          document.getElementById("conversation-list-reply-pane") ||
            document.getElementById("customer-card-container") ||
            document.body
        )}
    </EmojiPickerWrapper>
  );
};

export default CustomEmojiPicker;
